import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { GETUsers, GetAdresses } from '../../services/users';
import { CreateEnquiry } from '../../services/sellOldAC';
import { capitalizeWords } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import {
  darkDialogPaperProps,
  darkFieldSx,
  darkItemCardSx,
  darkMenuPaperSx,
  darkTitleSx,
} from '../../components/common/darkFormStyles';

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const initialAc = {
  brand: '',
  model: '',
  acType: '',
  tonnage: '',
  age: '',
  condition: '',
  technology: '',
};

const AddSellOldACRequest = ({ open, onClose, onSaved }: Props) => {
  const [users, setUsers] = useState<any[]>([]);
  const [userSearch, setUserSearch] = useState('');
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<any | null>(null);
  const [addresses, setAddresses] = useState<any[]>([]);
  const [addressId, setAddressId] = useState('');
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [date, setDate] = useState('');
  const [slot, setSlot] = useState('FIRST_HALF');
  const [propertyType, setPropertyType] = useState('RESIDENTIAL');
  const [oldAcDetails, setOldAcDetails] = useState<any[]>([{ ...initialAc }]);
  const [saving, setSaving] = useState(false);
  const { showSnackbar, SnackbarComponent } = useSnackbar();

  const userId = selectedUser?._id || '';

  // Customer list is search-driven (not a single fixed-size page) so
  // customers past the first 100 are actually reachable — a plain
  // `GETUsers({ page: 1, limit: 100 })` silently hid the rest.
  useEffect(() => {
    if (!open) return;
    const delay = setTimeout(() => {
      setUsersLoading(true);
      GETUsers({ page: 1, limit: 50, searchQuery: userSearch.trim() })
        .then((response) => setUsers(response.data || []))
        .finally(() => setUsersLoading(false));
    }, 400);

    return () => clearTimeout(delay);
  }, [userSearch, open]);

  useEffect(() => {
    if (!userId) {
      setAddresses([]);
      setAddressId('');
      return;
    }
    setAddressesLoading(true);
    GetAdresses(userId)
      .then((response) => setAddresses(response.data || []))
      .finally(() => setAddressesLoading(false));
  }, [userId]);

  const updateAc = (index: number, field: string, value: string) => {
    setOldAcDetails((prev) =>
      prev.map((item, itemIndex) =>
        itemIndex === index ? { ...item, [field]: value } : item,
      ),
    );
  };

  const addAc = () => setOldAcDetails((prev) => [...prev, { ...initialAc }]);
  const removeAc = (index: number) =>
    setOldAcDetails((prev) => prev.filter((_, itemIndex) => itemIndex !== index));

  const resetForm = () => {
    setSelectedUser(null);
    setUserSearch('');
    setAddressId('');
    setDate('');
    setSlot('FIRST_HALF');
    setPropertyType('RESIDENTIAL');
    setOldAcDetails([{ ...initialAc }]);
  };

  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  const handleSubmit = async () => {
    if (!userId || !addressId || !date || !slot) {
      showSnackbar('User, address, date and slot are required', 'error');
      return;
    }

    try {
      setSaving(true);
      await CreateEnquiry({
        user_id: userId,
        addressId,
        date,
        slot,
        subType: 'OLD_AC',
        propertyType,
        oldAcDetails,
      });
      showSnackbar('Sell old AC request created', 'success');
      onSaved?.();
      resetForm();
      onClose();
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Failed to create request', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={darkDialogPaperProps}>
      {SnackbarComponent}

      <DialogTitle sx={darkTitleSx}>
        Add Sell Old AC Request
        <IconButton onClick={handleClose} sx={{ color: '#9e9e9e' }} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogContent sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          <Autocomplete
            options={users}
            value={selectedUser}
            loading={usersLoading}
            onChange={(_event, newValue) => setSelectedUser(newValue)}
            onInputChange={(_event, newInputValue) => setUserSearch(newInputValue)}
            getOptionLabel={(option) =>
              `${capitalizeWords(option.name || '') || option.phoneNumber} - ${option.phoneNumber}`
            }
            isOptionEqualToValue={(option, value) => option._id === value._id}
            sx={darkFieldSx}
            slotProps={{ paper: darkMenuPaperSx }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Customer"
                placeholder="Search by name or phone"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {usersLoading ? <CircularProgress size={16} sx={{ color: '#E31E25' }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />

          <FormControl fullWidth disabled={!userId || addressesLoading} sx={darkFieldSx}>
            <InputLabel>Address</InputLabel>
            <Select
              value={addressId}
              label="Address"
              onChange={(event) => setAddressId(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              {addresses.map((address) => (
                <MenuItem key={address._id} value={address._id}>
                  {[address.house, address.street, address.city, address.zipcode].filter(Boolean).join(', ')}
                </MenuItem>
              ))}
              {!addressesLoading && userId && addresses.length === 0 && (
                <MenuItem disabled value="">
                  No saved addresses for this customer
                </MenuItem>
              )}
            </Select>
          </FormControl>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              type="date"
              label="Inspection Date"
              value={date}
              onChange={(event) => setDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{
                ...darkFieldSx,
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  filter: 'invert(1)',
                  cursor: 'pointer',
                },
              }}
            />
            <FormControl fullWidth sx={darkFieldSx}>
              <InputLabel>Slot</InputLabel>
              <Select
                value={slot}
                label="Slot"
                onChange={(event) => setSlot(event.target.value)}
                MenuProps={{ PaperProps: darkMenuPaperSx }}
              >
                <MenuItem value="FIRST_HALF">1st Half</MenuItem>
                <MenuItem value="SECOND_HALF">2nd Half</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <FormControl fullWidth sx={darkFieldSx}>
            <InputLabel>Place Type</InputLabel>
            <Select
              value={propertyType}
              label="Place Type"
              onChange={(event) => setPropertyType(event.target.value)}
              MenuProps={{ PaperProps: darkMenuPaperSx }}
            >
              <MenuItem value="RESIDENTIAL">Residential</MenuItem>
              <MenuItem value="COMMERCIAL">Commercial</MenuItem>
            </Select>
          </FormControl>

          {oldAcDetails.map((ac, index) => (
            <Box key={index} sx={darkItemCardSx}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography sx={{ color: '#fff', fontWeight: 600 }}>AC {index + 1}</Typography>
                {oldAcDetails.length > 1 && (
                  <IconButton size="small" onClick={() => removeAc(index)} sx={{ color: '#9e9e9e' }}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                {['brand', 'model', 'acType', 'tonnage', 'age', 'condition', 'technology'].map((field) => (
                  <TextField
                    key={field}
                    label={field.replace(/([A-Z])/g, ' $1').replace(/^./, (char) => char.toUpperCase())}
                    value={ac[field]}
                    onChange={(event) => updateAc(index, field, event.target.value)}
                    sx={darkFieldSx}
                  />
                ))}
              </Box>
            </Box>
          ))}

          <Button
            variant="outlined"
            onClick={addAc}
            sx={{
              alignSelf: 'flex-start',
              color: '#E31E25',
              borderColor: '#E31E25',
              borderRadius: '10px',
              '&:hover': { borderColor: '#B71C1C', backgroundColor: 'rgba(227, 30, 37, 0.08)' },
            }}
          >
            + Add Another AC
          </Button>
        </Box>
      </DialogContent>

      <Divider sx={{ borderColor: '#2a2a2a' }} />

      <DialogActions sx={{ p: 2.5 }}>
        <Button onClick={handleClose} disabled={saving} sx={{ color: '#9e9e9e' }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : null}
          sx={{
            background: '#E31E25',
            color: '#fff',
            px: 4,
            py: 1.1,
            borderRadius: '10px',
            fontWeight: 600,
            '&:hover': { background: '#C62828' },
            '&.Mui-disabled': { background: '#7a2a2a', color: '#ddd' },
          }}
        >
          {saving ? 'Saving…' : 'Create Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSellOldACRequest;
